import React, { ChangeEvent, FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { COLORS } from '../../utils/Colors';
import { API } from '../../utils/API';
import { Collector } from '../../models/Collector';
import { Router } from '../../router/Router';
import { useUIContext } from '../../context/UIContext';

const Root = styled.div`
    display: flex;
    flex-direction: column;
    margin-right: 40px;
    margin-block: 20px;
    
    @media screen and (max-width: 768px) {
        margin-right: 0;
        margin-block: 15px;
    }
    
    @media screen and (max-width: 480px) {
        margin-block: 10px;
    }
`;

const SearchBarContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 2rem;
    margin-bottom: 8px;
    padding: 20px 0;

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: stretch;
        gap: 1rem;
        padding: 15px 0;
    }
    
    @media (max-width: 480px) {
        padding: 10px 0;
        gap: 0.8rem;
    }
`;

const SearchTitle = styled.h2`
    font-size: 1.5rem;
    font-weight: 700;
    color: ${COLORS.DARK_GRAY};
    margin: 0;
    letter-spacing: -0.025em;

    @media (max-width: 768px) {
        font-size: 1.25rem;
        text-align: center;
    }
    
    @media (max-width: 480px) {
        font-size: 1.1rem;
    }
`;

const InputContainer = styled.div`
    flex: 1;
    max-width: 400px;
    position: relative;
    display: flex;
    align-items: center;
    background: ${COLORS.WHITE};
    border: 2px solid ${COLORS.VERY_LIGHT_GRAY};
    border-radius: 12px;
    padding: 0;
    transition: all 0.2s ease-in-out;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);

    &:hover {
        border-color: ${COLORS.LIGHT_GRAY};
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.08);
    }

    &:focus-within {
        border-color: ${COLORS.BLUE};
        box-shadow: 0 0 0 3px ${COLORS.BLUE_OPACITY};
        transform: translateY(-1px);
    }

    @media (max-width: 768px) {
        max-width: none;
    }
`;

const SearchInput = styled.input`
    flex: 1;
    padding: 14px 16px;
    border: none;
    outline: none;
    background: transparent;
    font-size: 14px;
    color: ${COLORS.DARK_GRAY};
    font-weight: 500;

    &::placeholder {
        color: ${COLORS.LIGHT_GRAY};
        font-weight: 400;
    }

    &:focus::placeholder {
        opacity: 0.7;
    }
    
    @media (max-width: 768px) {
        padding: 12px 14px;
        font-size: 13px;
    }
    
    @media (max-width: 480px) {
        padding: 10px 12px;
        font-size: 12px;
    }
`;

const SearchIcon = styled.i`
    padding: 16px;
    color: ${COLORS.LIGHT_GRAY};
    font-size: 16px;
    transition: all 0.2s ease-in-out;
    cursor: pointer;

    &:hover {
        color: ${COLORS.BLUE};
        transform: scale(1.1);
    }
    
    @media (max-width: 768px) {
        padding: 14px;
        font-size: 15px;
    }
    
    @media (max-width: 480px) {
        padding: 12px;
        font-size: 14px;
    }
`;

const TableContainer = styled.div`
    margin-top: 10px;
    max-height: 500px;
    overflow-y: auto;
    overflow-x: auto;
    border-radius: 12px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    background: ${COLORS.WHITE};
    border: 1px solid ${COLORS.VERY_LIGHT_GRAY};
    -ms-overflow-style: none;
    scrollbar-width: none;

    &::-webkit-scrollbar {
        display: none;
    }
    
    @media screen and (max-width: 768px) {
        max-height: 450px;
        border-radius: 8px;
        margin-top: 8px;
    }
    
    @media screen and (max-width: 480px) {
        max-height: 400px;
        border-radius: 6px;
        margin-top: 6px;
    }
`;

const StyledTable = styled.table`
    width: 100%;
    border-collapse: collapse;
`;

const TableHeader = styled.thead`
    background: linear-gradient(135deg, ${COLORS.BLUE} 0%, ${COLORS.GREEN} 100%);
    color: ${COLORS.WHITE};
    position: sticky;
    top: 0;
    z-index: 10;
`;

const TableHeaderCell = styled.th`
    padding: 16px 20px;
    text-align: left;
    font-weight: 600;
    font-size: 14px;
    letter-spacing: 1px;
    text-transform: uppercase;
    border-bottom: 2px solid ${COLORS.BLUE};

    &:first-child {
        border-top-left-radius: 12px;
    }

    &:last-child {
        border-top-right-radius: 12px;
    }

    @media (max-width: 768px) {
        padding: 10px 12px;
        font-size: 11px;
        letter-spacing: 0.5px;
    }
    
    @media (max-width: 480px) {
        padding: 8px 10px;
        font-size: 10px;
        letter-spacing: 0.3px;
    }
`;

const TableBody = styled.tbody`
    background: ${COLORS.WHITE};
`;

const TableRow = styled.tr<{ isEven: boolean }>`
    background: ${(props) => (props.isEven ? COLORS.WHITE : COLORS.DULL)};
    transition: all 0.2s ease-in-out;
    border-bottom: 1px solid ${COLORS.VERY_LIGHT_GRAY};

    &:hover {
        background: ${COLORS.BLUE_OPACITY};
        transform: translateY(-1px);
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        cursor: pointer;
    }

    &:last-child {
        border-bottom: none;
    }
`;

const TableCell = styled.td`
    padding: 16px 20px;
    font-size: 14px;
    color: ${COLORS.DARK_GRAY};
    vertical-align: middle;
    border-right: 1px solid ${COLORS.VERY_LIGHT_GRAY};

    &:last-child {
        border-right: none;
    }

    @media (max-width: 768px) {
        padding: 10px 12px;
        font-size: 12px;
    }
    
    @media (max-width: 480px) {
        padding: 8px 10px;
        font-size: 11px;
    }
`;

const ActionButton = styled.button`
    background: linear-gradient(135deg, ${COLORS.BLUE} 0%, ${COLORS.LIGHT_GREEN_2} 100%);
    color: ${COLORS.WHITE};
    border: none;
    padding: 8px 16px;
    border-radius: 6px;
    font-weight: 500;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.2s ease-in-out;
    text-decoration: none;
    min-width: 80px;
    text-align: center;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(75, 177, 248, 0.3);
        background: linear-gradient(135deg, ${COLORS.BLUE} 0%, ${COLORS.GREEN} 100%);
    }

    &:active {
        transform: translateY(0);
    }
    
    @media screen and (max-width: 768px) {
        padding: 6px 12px;
        font-size: 12px;
        min-width: 70px;
    }
    
    @media screen and (max-width: 480px) {
        padding: 4px 8px;
        font-size: 11px;
        min-width: 60px;
    }
`;

export const DashboardComponent: FC = () => {
  const navigation = useNavigate();

  const { loader } = useUIContext();

  const [searchValue, setSearchValue] = useState<string>('');
  const [collectors, setCollectors] = useState<Collector[]>([]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  };

  const fetchCollectors = async () => {
    loader.showLoader();
    try {
      const response = await API.Users.listCollectors();
      setCollectors(response.collectors);
    } catch (error) {
      console.error(error);
    } finally {
      loader.hideLoader();
    }
  };
  const handleSelectColletor = (collector: Collector) => Router(navigation).goToCollectorDetails(collector.id);

  useEffect(() => {
    fetchCollectors().finally(() => undefined);
  }, []);

  return (
    <Root>
      <SearchBarContainer>
        <SearchTitle>Cube List</SearchTitle>
        <InputContainer>
          <SearchInput value={searchValue} onChange={handleChange} placeholder="Search cube..." />
          <SearchIcon className="fa-solid fa-magnifying-glass" />
        </InputContainer>
      </SearchBarContainer>
      {
        collectors.length > 0 && (
          <TableContainer>
            <StyledTable>
              <TableHeader>
                <tr>
                  <TableHeaderCell>Cube Serial Number</TableHeaderCell>
                  <TableHeaderCell>Cube name</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                  <TableHeaderCell>Operational status</TableHeaderCell>
                </tr>
              </TableHeader>
              <TableBody>
                {
                  collectors
                    .filter((collector) => collector.name.toLowerCase().includes(searchValue.toLowerCase()))
                    .map((collector: Collector, index: number) => (
                      <TableRow key={collector.id} isEven={index % 2 === 0}>
                        <TableCell>
                          <ActionButton
                            type="button"
                            onClick={() => handleSelectColletor(collector)}
                          >{collector.hw_serial_num || 'N/A'}
                          </ActionButton>
                        </TableCell>
                        <TableCell>{collector.name}</TableCell>
                        <TableCell>{collector.status}</TableCell>
                        <TableCell>{collector.operational_status}</TableCell>
                      </TableRow>
                    ))
                }
              </TableBody>
            </StyledTable>
          </TableContainer>
        )
      }
    </Root>
  );
};
